import { Injectable, isDevMode } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, Router } from '@angular/router';

import { Observable, of, from } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { VideoPortalUser } from '../auth/video-portal-user.model';
import { map, take, catchError, filter } from 'rxjs/operators';

@Injectable()
export class AuthGuardService implements CanActivate {
    constructor(private authService: AuthService, private router: Router) {
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        // AppComponent ensures initMSAL is called.
        // We just need to wait for the *first* result emitted by userInfoSubject
        // after the initialization attempt has concluded.
        return this.authService.userInfoSubject.pipe(
            // Filter out the initial null value *if* a login/restore is actively in progress.
            // This prevents the guard from acting on the initial null before the actual result arrives.
            filter(user => {
                const initPending = this.authService.initializationPromise != null;
                const loginProcessing = this.authService.isLoginSuccessInProgress;
                const shouldWait = (initPending || loginProcessing) && user === null;
                if (isDevMode() && shouldWait) {
                    console.log('AuthGuard: Filtering initial null because initialization/login is in progress.');
                }
                return !shouldWait;
            }),
            take(1), // Take the first non-filtered value (null or User)
            map(user => {
                // This map runs *after* the first relevant value is emitted.
                if (isDevMode()) {
                    console.log('AuthGuard: First relevant userInfoSubject value received, proceeding to isAuthorised check.', user);
                }
                return this.isAuthorised(route, state, user);
            }),
            catchError((err) => {
                 if (isDevMode()) {
                    console.error('AuthGuard: Error in userInfoSubject stream.', err);
                }
                // Handle potential errors in the stream itself
                this.router.navigate(['/error']);
                return of(false);
            })
        );
    }

    private isAuthorised(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, user: VideoPortalUser | null): boolean {
        if (!user) {
            // If after the first *relevant* emission we still have no user,
            // then authentication failed or no session exists.
            // MsalGuard should handle actual login redirects if configured.
            if (isDevMode()) {
                console.warn('AuthGuard: isAuthorised check found null user. Denying access or redirecting.');
            }
            // Decide where to redirect if user is truly null after init
            // Example: Redirect to login or home. Assuming MsalGuard handles actual login prompt.
            this.router.navigate(['/home']); // Or potentially trigger this.authService.login() if MsalGuard isn't active?
            return false;
        }
        // Check T&Cs first - redirect if not agreed
        if (!user.hasAgreedToTermsAndConditions) {
             if (isDevMode()) {
                console.log('AuthGuard: User has not agreed to T&Cs. Redirecting.');
             }
             this.router.navigate(['/terms-and-conditions'], { queryParams: { returnUrl: state.url } });
             return false;
        }

        // Check Roles
        const roles = route.data.roles as string[];
        const isAuthorizedForRoute = !roles || roles.length === 0 || roles.some(role => user.isInRole(role));

        if (!isAuthorizedForRoute) {
             if (isDevMode()) {
                console.log(`AuthGuard: User does not have required roles (${roles}). Redirecting.`);
             }
             // Optional: Redirect to an 'unauthorized' page or home
             this.router.navigate(['/home']);
             return false;
        }

         if (isDevMode()) {
            console.log('AuthGuard: User is authorized for the route.');
        }
        return true; // User is authenticated, agreed to T&Cs, and has the required role
    }
}
